import {
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    inject,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    IAccessRightFilterCollection,
    ILightWeightBusinessUnit,
    ILightWeightObject,
    ILightWeightUser,
} from '@fi-shared/models';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { CheckpointActionRecipientType } from '@fi-shared/checklist/checklist-enum';
import { IAutomatedActionSharingItemsListData } from './action-sharing-items-list-dialog/action-sharing-items-list-dialog.component';
import { FiOnboardingStepNotificationRecipientType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiAutomatedActionSharingItemsListDialogComponent } from './action-sharing-items-list-dialog/action-sharing-items-list-dialog.component';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiQuizNotificationRecipientType } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-enums';

type RecipientTypesEnum =
    | typeof CheckpointActionRecipientType
    | typeof FiOnboardingStepNotificationRecipientType
    | typeof FiQuizNotificationRecipientType;
type RecipientTypeValue = CheckpointActionRecipientType | FiOnboardingStepNotificationRecipientType;

@Directive()
export class FiAutomatedActionBase implements OnInit, OnChanges {
    @Input({ required: true }) isCreateTaskAction: boolean;

    protected currentRecipientTypes: RecipientTypesEnum;
    protected _objectType: ObjectTypes;
    @Input({ required: true }) set objectType(value: ObjectTypes) {
        this._objectType = value;
        if (value === ObjectTypes.Checklist2) {
            this.currentRecipientTypes = CheckpointActionRecipientType;
        } else if (value === ObjectTypes.Onboarding) {
            this.currentRecipientTypes = FiOnboardingStepNotificationRecipientType;
        } else if (value === ObjectTypes.Quiz) {
            this.currentRecipientTypes = FiQuizNotificationRecipientType;
        }
    }
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() recipientType: RecipientTypeValue;
    @Input() recipientUnitIds: number[] = [];
    @Input() canEditDeleteAction: boolean = true;

    @Input() selectedItemIds: number[];
    @Input() selectedContacts: string[];

    @Output() editAction = new EventEmitter<void>();
    @Output() deleteAction = new EventEmitter<void>();
    @Output() archiveAction = new EventEmitter<void>();

    readonly fiInfoPosition: string = 'bottom';
    readonly shownSharingItemsMaxCount: number = 5;
    displayedSharingItems: IAutomatedActionSharingItemsListData[] = [];
    displayedSelectedUnits: IAutomatedActionSharingItemsListData[] = [];
    protected fiDialogsService = inject(FiDialogsService);
    protected appTextService = inject(AppTextService);
    protected cdr = inject(ChangeDetectorRef);
    protected injector = inject(Injector);

    public ngOnInit(): void {
        this.setDisplayedSharingItems();
        this.setDisplayedUnits();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedItemIds || changes.selectedItemValues) {
            this.setDisplayedSharingItems();
        }
        if (changes.recipientUnitIds) {
            this.setDisplayedUnits();
        }
    }

    protected setDisplayedUnits(): void {
        this.displayedSelectedUnits = this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits.filter(
            (businessUnit: ILightWeightBusinessUnit) => this.recipientUnitIds.includes(businessUnit.Id),
        ).map((businessUnit: ILightWeightBusinessUnit) => ({ Name: businessUnit.Name }));
    }

    protected setDisplayedSharingItems(): void {
        this.displayedSharingItems = this.convertSelectedItemsToDisplayedItems();
        this.cdr.markForCheck();
    }

    protected showActionSharingFullListDialog(items: IAutomatedActionSharingItemsListData[] | string[]): void {
        this.fiDialogsService.custom({
            contentComponentRef: FiAutomatedActionSharingItemsListDialogComponent,
            contentComponentData: this.getDisplayedItems(items),
            isHeaderCloseButtonShown: true,
            hideDialogTopBorder: true,
            hideFooter: true,
            config: {
                maxHeight: '300px',
                height: 'fit-content',
                panelClass: 'action-sharing-items-list-dialog-panel',
                autoFocus: false,
            },
        });
    }

    protected getDisplayedItems(
        items: IAutomatedActionSharingItemsListData[] | string[],
        isLimited?: boolean,
    ): IAutomatedActionSharingItemsListData[] | string[] {
        return isLimited ? items.slice(0, this.shownSharingItemsMaxCount) : items;
    }

    protected isOthersLabelShown(items: IAutomatedActionSharingItemsListData[]): boolean {
        return items.length > this.shownSharingItemsMaxCount;
    }

    protected getOthersLabel(items: IAutomatedActionSharingItemsListData[]): string {
        return this.appTextService.get('and_others', {
            '{0}': items.length - this.shownSharingItemsMaxCount,
        });
    }

    protected convertSelectedItemsToDisplayedItems(): IAutomatedActionSharingItemsListData[] {
        if (!this.selectedItemIds?.length && !this.selectedContacts?.length) {
            return [];
        }

        switch (this.recipientType) {
            case this.currentRecipientTypes.EmailAddress:
            case this.currentRecipientTypes.SMS:
                return this.selectedContacts.map((selectedContact: string) => {
                    return { Name: selectedContact };
                });
            case this.currentRecipientTypes.Roles:
            case this.currentRecipientTypes.RolesLocal:
                return this.selectedItemIds.map((selectedItemId: number) => {
                    return {
                        Name: this.accessRightFilterCollection.AccessRightCoreFilter.Roles.find(
                            (role: ILightWeightObject) => role.Id === selectedItemId,
                        )?.Name,
                    };
                });
            case this.currentRecipientTypes.Users:
                return this.selectedItemIds.map((selectedItemId: number) => {
                    const selectedUser: ILightWeightUser =
                        this.accessRightFilterCollection.AccessRightUserFilter.Users.find(
                            (user: ILightWeightUser) => user.Id === selectedItemId,
                        );
                    return {
                        Name: selectedUser?.FullName,
                        ProfileData: selectedUser,
                    };
                });
        }
    }

    protected isSelectedItemsTextShown(): boolean {
        return this.isUserRecipientType() || this.isRoleRecipientType() || this.isLocalRolesRecipientType();
    }

    protected isUserRecipientType(): boolean {
        return this.recipientType === this.currentRecipientTypes.Users;
    }

    protected isRoleRecipientType(): boolean {
        return this.recipientType === this.currentRecipientTypes.Roles;
    }

    protected isEmailRecipientType(): boolean {
        return this.recipientType === this.currentRecipientTypes.EmailAddress;
    }

    protected isPhoneNumberRecipientType(): boolean {
        return this.recipientType === this.currentRecipientTypes.SMS;
    }

    protected isLocalRolesRecipientType(): boolean {
        return this.recipientType === this.currentRecipientTypes.RolesLocal;
    }

    protected isParticipantsRecipientType(): boolean {
        return (
            this._objectType === ObjectTypes.Onboarding &&
            this.recipientType === FiOnboardingStepNotificationRecipientType.Participants
        );
    }

    protected onEditActionButtonClick(): void {
        this.editAction.emit();
    }

    protected onArchiveActionClick(): void {
        this.archiveAction.emit();
    }

    protected onDeleteActionClick(): void {
        this.deleteAction.emit();
    }
}
